import { useTranslations } from '@vocab/react';
import { Notice, Stack, Text, TextLink } from 'braid-design-system';
import type { ComponentProps, FC } from 'react';

import { useFeatureFlag } from 'src/App/CandidateManagementPage/FeatureFlagsContext';

import translations from './.vocab';
import { VerificationsEmptyState } from './VerificationsEmptyState';
import { VerificationsErrorState } from './VerificationsErrorState';
import { VerificationsLoadingState } from './VerificationsLoadingState';
import {
  type VerificationsQueryVariables,
  useVerificationsQuery,
} from './VerificationsQuery.hirer.generated';
import { VerificationsResultState } from './VerificationsResultState';

interface Props
  extends Pick<
    ComponentProps<typeof VerificationsResultState>,
    | 'onCloseVerificationsAlert'
    | 'onClickVerificationsAlertLearnMore'
    | 'onClickVerificationsTabFooterLearnMore'
  > {
  queryInput: VerificationsQueryVariables['input'];
  onClickWhyThisCouldHappen: () => void;
}

export const Verifications: FC<Props> = ({
  onCloseVerificationsAlert,
  onClickVerificationsAlertLearnMore,
  onClickVerificationsTabFooterLearnMore,
  onClickWhyThisCouldHappen,
  queryInput: { adcentreProspectId, jobId },
}) => {
  const { t } = useTranslations(translations);
  const { data, error, loading } = useVerificationsQuery({
    variables: { input: { adcentreProspectId, jobId } },
  });

  const { variant } = useFeatureFlag('cm-sg-verified-career-history-notice');

  if (loading) {
    return <VerificationsLoadingState />;
  }

  if (error || !data) {
    return <VerificationsErrorState />;
  }

  if (
    data.applicationByAdcentreProspectId.__typename === 'ResponseError' ||
    data.applicationByAdcentreProspectId.result.profile.__typename ===
      'ResponseError'
  ) {
    return <VerificationsErrorState />;
  }

  if (!data.applicationByAdcentreProspectId.result.profile.result) {
    return <VerificationsEmptyState />;
  }

  if (
    data.applicationByAdcentreProspectId.result.profile.result.verifications
      .__typename === 'ResponseError'
  ) {
    return <VerificationsErrorState />;
  }

  const verifications =
    data.applicationByAdcentreProspectId.result.profile.result.verifications
      .result;

  if (
    !verifications.verifiedIdentity &&
    verifications.workHistory.length === 0 &&
    verifications.education.length === 0 &&
    verifications.licences.length === 0 &&
    verifications.rightToWork.length === 0
  ) {
    return <VerificationsEmptyState />;
  }

  return (
    <VerificationsResultState
      onCloseVerificationsAlert={onCloseVerificationsAlert}
      onClickVerificationsAlertLearnMore={onClickVerificationsAlertLearnMore}
      onClickVerificationsTabFooterLearnMore={
        onClickVerificationsTabFooterLearnMore
      }
      verifications={verifications}
      careerHistoryNotice={
        variant === 'on' ? (
          <Notice tone="info">
            <Stack space="xsmall">
              <Text size="small">
                {t(
                  "Some role details may be different from what you see in the applicant's profile",
                )}
              </Text>
              <Text size="small">
                <TextLink
                  href="https://help.sg.employer.seek.com/s/article/Understanding-Employment-Data-in-Singapore-Government-Careers-Skills-Passport"
                  onClick={onClickWhyThisCouldHappen}
                  target="_blank"
                  rel="noopener"
                >
                  {t('Why this could happen?')}
                </TextLink>
              </Text>
            </Stack>
          </Notice>
        ) : null
      }
    />
  );
};
